import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import NSJobComponent from '../../job.js';

import JobSchema from '../../../../models/job-schema.js';
import Company from '../../../../models/company.js';
import Invitation from '../../../../models/invitation.js';
import Person from '../../../../models/person.js';

import { personDisplayName } from '../../../../helpers/person.js';
import { callLambda, getActionFromLambdaResults } from '../../../../helpers/lambda.js';
import { runBehindShutter } from '../../../../helpers/reload-behind-shutter.js';
import { nsLoaderContext } from '../../../../ns-services/contexts.js';
import type { NSLoader } from '../../../../ns-services/ns-loader-service.js';
import { consume } from '@lit/context';

import '../../../../ns-elements/ns-job-layout.js';
import definition from '@job-schemas/company/accept-company-request-to-join.json';
import { jobSchema } from '../../job-schema.js';

@customElement(NSAcceptCompanyRequestToJoin.is)
export default class NSAcceptCompanyRequestToJoin extends NSJobComponent {
  static get is() {
    return 'ns-accept-company-request-to-join';
  }

  // Closes over the page before it reloads into the new company
  @consume({ context: nsLoaderContext })
  private __loader?: NSLoader;

  @state()
  private __invitation?: Invitation;

  @state()
  private __inviter?: Person;

  @state()
  private __company?: Company;

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { accept: { type: 'button', label: 'Accept' } } });
  }

  async connectedCallback() {
    try {
      await super.connectedCallback();
      this.loading('Loading invitation details');

      if (!this._db) throw new Error('Unable to find db service');

      if (!this._directory) throw new Error('Unable to find NSDirectoryService');

      await this._directory.initialised;

      if (!this._auth) throw new Error('Unable to find auth service');

      if (!this._job) throw new Error('Unable to find job card');
      const workflowId = this._job.workflow.id as string;
      const workflow = await this._db.getById('workflow', workflowId);
      if (!workflow) throw new Error('Could not fetch parent workflow of the job');

      const invitationId = this._job.inputValues.invitationId as string;
      if (invitationId) {
        this.__invitation = await this._db.getById('invitation', invitationId);
      } else {
        const queryCall = await this._db.query('invitation', {
          'invitee.personId': {
            $eq: this._directory.me?.id,
          },
        });
        const invitations = queryCall.results as unknown as Invitation[];
        [this.__invitation] = invitations;
      }
      if (!this.__invitation) throw new Error(`Unable to fetch invitation with ID: ${invitationId}`);

      const inviterId = this.__invitation.inviter.personId as string;
      this.__inviter = await this._db.getById('people', inviterId);
      if (!this.__inviter) throw new Error(`Unable to fetch inviter with ID: ${inviterId}`);

      const companyId = this.__invitation?.invitee.company.id as string;
      this.__company = await this._db.getById('company', companyId);
      if (!this.__company) throw new Error(`Unable to fetch company with ID: ${companyId}`);

      this.loaded();
    } catch (err: unknown) {
      this.loaded();
      this.failJob(err, 'Unexpected error occurred while loading companies');
    }
  }

  async _outcomeAcceptHandler() {
    if (!this._job || !this._job.id) {
      this.triggerCardError('Unable to find job card');
      return;
    }

    this.clearError();
    try {
      if (!this._db) throw new Error('Missing required db service');
      if (!this._directory || !this._directory.me || !this._directory.me.id)
        throw new Error('Missing directory service');
      if (!this.__company || !this.__company.id) throw new Error('Missing company');
      if (!this.__invitation || !this.__invitation.id) throw new Error('Missing invitation');
      if (!this._auth || !this._auth.token) throw new Error('Missing auth token');
      if (!this._job || !this._job.workflow || !this._job.workflow.id) throw new Error('Missing required job workflow');

      const profile = this._directory.me;
      if (!profile || !profile!.id) throw new Error('Missing required person ID');
      const directory = this._directory;
      const token = this._auth.token;
      const invitationId = this.__invitation.id;

      // The shutter closes first; joining, completing this card and switching company happen behind it, then the app
      // reloads, since crag's store still holds the previous company's entities, some under another policy's projection
      await runBehindShutter(this.__loader, `Joining ${this.__company.name ?? 'the company'}…`, async () => {
        const res = await callLambda('company/join', 'POST', token, JSON.stringify({ invitationId }));
        if (res.res.code !== 200 && typeof res.res.message === 'string') throw new Error(res.res.message);
        const action = await getActionFromLambdaResults(res, 'company_joined');
        if (typeof action === 'string') throw new Error('Unexpected action type');

        await this.outcome('accept');
        await directory.selectCompanyProfile(action.id);
      });
    } catch (err: unknown) {
      this.showError(err);
    }
  }

  render() {
    const inviterName = this.__inviter ? personDisplayName(this.__inviter) : '';
    const invited = inviterName ? `${inviterName} has invited you` : "You've been invited";
    const heading = `${invited} to join ${this.__company?.name ?? 'their company'}`;

    return html`
      <ns-job-layout
        heading=${heading}
        image="/images/jobs/companies-house-verification.svg"
        image-label="Company invitation illustration"
      >
        <div class="job-panel">
          <p>
            Please accept the invitation and join ${this.__company?.name ?? 'the company'} to be able to continue using
            nodeStream.
          </p>
        </div>
      </ns-job-layout>
    `;
  }
}
